// 유사기업 조회 — officejs ValuationPage 의 3개 흐름을 한 페이지 3탭으로 이식.
//   ① 사업내용 AI: 전 상장사 사업보고서 키워드 매칭 + AI 스코어링 (2~7분)
//   ② 산업분류(KSIC): 추천 → 선택 → 4단계 스크리닝 (증권신고서 표준)
//   ③ 공시 선례: 주요사항보고서 외부평가기관 평가의견서 검색 (동기)
// 엑셀 라이브 시트(officejs 전용) 대신 결과를 화면 표·아코디언으로 그리고,
// 엑셀·PDF 는 서버 생성 presigned URL 을 연다.
import { downloadBase64, XLSX_MIME } from "../lib/download";
import { createContext, Fragment, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RelativeLink from "../components/peer/RelativeLink";
import { AddRelativePeersContext, type AddRelativePeers } from "../components/peer/relativePeerContext";
import { RELATIVE_ENABLED } from "../lib/relative";
import { recordLabFile } from "../lib/labFiles";
import { precedentSearchQuery, recordPeerRecent, recordPrecedentSearch, usePeerRecent, type PeerRecent } from "../lib/peerRecent";
import { labGet, labHeartbeat, labSet, labStopHeartbeat, useLabState } from "../lib/labState";
import { libDownload, libPreviewUrl } from "../lib/library";
import { PanelClose, SidePanel } from "../ui/SidePanel";
import LiveSheet, { industryStageLines, peerFinalLines, peerPartialLines, useLiveSheet } from "../components/lab/LiveSheet";
import { Button } from "../ui/Button";
import { CompanyLogo } from "../ui/CompanyLogo";
import {
  elapsedLabel, fmtNum, industryExcel, industryPdf, industryScreenStart, industrySuggest,
  industryCatalog, peerCancel, peerExcel, peerPdf, peerResult, peerStart, peerStatusPath, pollTask, precedentExcel,
  precedentSearch, searchCompany,
  type IndustrySuggestion, type PollHandle,
} from "../lib/labApi";
import { HEARTBEAT_RE, PEER_STEP_TITLES, STEP_RE, buildStepRows } from "../lib/labSteps";
import "../styles/lab.css";

type Tab = "ai" | "industry" | "precedent";
const TABS: { key: Tab; label: string }[] = [
  { key: "ai", label: "유사기업 (사업내용 AI)" },
  { key: "industry", label: "산업분류 (KSIC)" },
  { key: "precedent", label: "공시 선례" },
];

// 우측 미리보기 패널 — 버핏 PDF 패널과 같은 UX: 만들면 다운로드로 튕기지 않고
// 오른쪽에 나란히 뜬다 (사용자 지정). 엑셀은 서버 HTML 렌더(libExcelViewUrl)로
// 미리보고, 다운로드·새 창은 패널 머리의 액션으로 뺐다.
type PeerPreview = { url: string; kind: "pdf" | "excel"; title: string; sub?: string };
const PeerPreviewCtx = createContext<(p: PeerPreview) => void>(() => {});
const PeerNavigationCtx = createContext({search: "", completed: (_tab: Tab, _rid: string) => {}});

/** 지난 조회 항목의 주소 — AI·산업분류는 rid 스냅샷 복원, 공시 선례는 검색 조건으로 다시 검색 */
function recentHref(r: PeerRecent): string {
  if (r.tab === "precedent") return `?tab=precedent&${precedentSearchQuery(r.precedent || { query: r.label })}`;
  return `?tab=${r.tab}&rid=${encodeURIComponent(r.rid)}`;
}

/** 지난 조회 목록 — 완료된 조회는 rid 고유 주소로 영구 보존되므로 눌러서 복원한다.
 *  데스크톱은 좌측 세로 패널(버핏 '저장된 분석'과 같은 자리), 모바일은 아래에서
 *  올라오는 시트(버핏 bfx-side 와 같은 패턴 — onPick 으로 시트를 닫는다). */
function PeerRecentSide({ onPick, onSelect }: { onPick: () => void; onSelect?: (entry: PeerRecent) => void }) {
  const list = usePeerRecent();
  const navigate = useNavigate();
  const when = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" :
      `${d.getMonth() + 1}.${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  return (
    <>
      <div className="peer-side-head">
        <span className="peer-side-title">지난 조회</span>
        <span className="peer-side-count">{list.length}건</span>
      </div>
      <div className="peer-side-list">
        {list.length === 0 ? (
          <div className="peer-side-empty">아직 조회 기록이 없습니다.<br />조회가 완료되면 여기에 쌓입니다.</div>
        ) : list.map((r) => (
          <button key={r.rid} type="button" className="peer-side-item"
            onClick={() => { if (onSelect) onSelect(r); else navigate(`/peer${recentHref(r)}`); onPick(); }}>
            <span className="peer-side-meta">
              <span className={`peer-side-badge ${r.tab}`}>{r.tab === "ai" ? "AI 선정" : r.tab === "industry" ? "산업분류" : "공시 선례"}</span>
              {typeof r.count === "number" && r.count > 0 ? (
                <span className="peer-side-cnt">{r.count}{r.tab === "precedent" ? "건" : "개사"}</span>
              ) : null}
              <span className="peer-side-dt">{when(r.created)}</span>
            </span>
            <span className="peer-side-lbl">{r.label}</span>
          </button>
        ))}
      </div>
    </>
  );
}

export default function PeerPage({embedded=false, onAddPeers}:{embedded?:boolean;onAddPeers?:AddRelativePeers}) {
  const [preview, setPreview] = useState<PeerPreview | null>(null);
  const [, setRootResizing] = useState(false);
  const [tab, setTab] = useLabState<Tab>("peer.tab", "ai");
  // 모바일 — 지난 조회를 아래에서 올라오는 시트로 (버핏 bfx-side 와 같은 패턴)
  const [mSide, setMSide] = useState(false);
  const sideTouchY = useRef(0);
  const sideGrabTouch = {
    onTouchStart: (e: React.TouchEvent) => { sideTouchY.current = e.touches[0].clientY; },
    onTouchEnd: (e: React.TouchEvent) => {
      if (e.changedTouches[0].clientY - sideTouchY.current > 60) setMSide(false);
    },
  };
  const location = useLocation();
  const [localSearch, setLocalSearch] = useState("");
  const search = embedded ? localSearch : location.search;
  const navigation = {search, completed: (kind:Tab, rid:string) => {
    const query = `?tab=${kind}&rid=${encodeURIComponent(rid)}`;
    if (embedded) setLocalSearch(query);
    else if (window.location.pathname === "/peer") window.history.replaceState(window.history.state, "", `/peer${query}`);
  }};
  useEffect(() => {
    const t = new URLSearchParams(search).get("tab");
    if (t === "ai" || t === "industry" || t === "precedent") setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (embedded) return;
    document.title = "유사기업 조회 · Valuation Agent";
    return () => { document.title = "Valuation Agent · DeepSearch"; };
  }, [embedded]);

  return (
    <PeerNavigationCtx.Provider value={navigation}><AddRelativePeersContext.Provider value={onAddPeers}>
      {!embedded && <header className="lab-top">
        <h1>유사기업 조회</h1>
        <span className="lab-sub">사업내용 AI · 산업분류 · 외부평가 선례</span>
        {RELATIVE_ENABLED && <Link to="/peer/relative" className="lab-chip" style={{marginLeft:"auto"}}>상대가치평가</Link>}
      </header>}
      <div className={`peer-shell${embedded ? " peer-shell-embedded" : ""}${mSide ? " m-side" : ""}`}>
      {embedded ? <details className="peer-embedded-history"><summary>지난 유사기업 조회</summary>
        <PeerRecentSide onPick={() => {}} onSelect={r => {setTab(r.tab);setLocalSearch(recentHref(r));}} />
      </details> : <>
      {/* 모바일 — 지난 조회 시트 여는 버튼 + 딤 (데스크톱에선 CSS 가 숨긴다) */}
      <button type="button" className="peer-side-open" onClick={() => setMSide(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" />
          <path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" />
        </svg>
        지난 조회
      </button>
      <div className="peer-dim" onClick={() => setMSide(false)} aria-hidden="true" />
      <aside className="peer-side">
        <button type="button" className="peer-grab" aria-label="닫기"
          onClick={() => setMSide(false)} {...sideGrabTouch} />
        <PeerRecentSide onPick={() => setMSide(false)} />
      </aside>
      </>}
      <div className={embedded ? "peer-embedded-body" : "lab-scroll"}><div className="lab-body">
      {!embedded && <section className="lab-card bf-intro">
        <h2 className="bf-intro-title">평가에 쓸 유사기업, 세 가지 방법으로 찾습니다</h2>
        <p className="bf-intro-lead">
          밸류에이션의 출발점은 비교기업 선정입니다 — AI 가 전체 상장사의 사업보고서를 읽고
          사업이 닮은 회사를 고르거나, 증권신고서 표준인 산업분류(KSIC) 4단계 스크리닝으로
          걸러내거나, 실제 합병·영업양수도 공시에서 외부평가기관이 썼던 선례를 그대로
          찾아볼 수 있습니다. 결과는 보고서 형식의 엑셀·PDF 로 바로 내려받습니다.
        </p>
        <div className="lab-chips">
          <span className="lab-chip soft">사업내용 AI — 전 상장사 매칭·스코어링</span>
          <span className="lab-chip soft">산업분류 KSIC — 증권신고서 표준 4단계</span>
          <span className="lab-chip soft">공시 선례 — 외부평가기관 실사용 유사기업</span>
        </div>
      </section>}
      <div className="lab-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button"
            className={`lab-tab${tab === t.key ? " on" : ""}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <PeerPreviewCtx.Provider value={setPreview}>
        {tab === "ai" && <PeerAiTab />}
        {tab === "industry" && <IndustryTab />}
        {tab === "precedent" && <PrecedentTab />}
      </PeerPreviewCtx.Provider>
      </div></div>
      {preview && (
        <SidePanel
          label="파일 미리보기"
          className="peer-preview-panel"
          onResizingChange={setRootResizing}
          titles={
            <>
              <div className="rp-title">{preview.title}</div>
              {preview.sub ? <div className="rp-sub">{preview.sub}</div> : null}
            </>
          }
          actions={
            <>
              {/* S3 는 크로스오리진이라 download 속성이 무시된다 — blob 저장(버핏 패널과 동일) */}
              <button className="lab-chip" type="button"
                onClick={() => void libDownload(preview.url,
                  decodeURIComponent((preview.url.split("?")[0].split("/").pop() || "")) ||
                  `${preview.title}.${preview.kind === "excel" ? "xlsx" : "pdf"}`)}>
                다운로드 ↓
              </button>
              <a className="lab-chip"
                 href={libPreviewUrl(preview.kind === "excel" ? "excel" : "report", preview.url) || preview.url}
                 target="_blank" rel="noreferrer">새 창 ↗</a>
              <PanelClose label="미리보기 닫기" onClick={() => setPreview(null)} />
            </>
          }
        >
          <div className="rp-body">
            <iframe src={libPreviewUrl(preview.kind === "excel" ? "excel" : "report", preview.url) || preview.url}
                    title={preview.title} />
          </div>
        </SidePanel>
      )}
      </div>
    </AddRelativePeersContext.Provider></PeerNavigationCtx.Provider>
  );
}

// ── 사업내용 AI: filter_log → 진행 로그 단계별 상세 ─────────────────
// 백엔드 filter_log 의 step 접두(2_, 2.5_, 3_, 4b_, 5_)를 화면 6단계에 붙인다.
// 행을 누르면 그 단계가 무엇을 걸렀는지(제외 기업·건수) 엑셀 조서처럼 보인다.
const _AI_STEP_OF: Array<[RegExp, number]> = [
  [/^1/, 2], [/^2\.5/, 3], [/^2/, 2], [/^3/, 4], [/^4/, 5], [/^5/, 6],
];

function aiStepDetails(result: PeerResult | null): Record<number, ReactNode> | undefined {
  const logs = (result as { filter_log?: Array<Record<string, unknown>> } | null)?.filter_log;
  if (!logs?.length) return undefined;
  const out: Record<number, ReactNode[]> = {};
  for (const entry of logs) {
    const step = String(entry.step || "");
    const no = (_AI_STEP_OF.find(([re]) => re.test(step)) || [null, 6])[1] as number;
    const names = (list: unknown, key = "name") =>
      Array.isArray(list)
        ? list.map((x) => String((x as Record<string, unknown>)?.[key] ?? x)).filter(Boolean)
        : [];
    const excluded = names(entry.excluded);
    const notSelected = Array.isArray(entry.not_selected) ? entry.not_selected as Array<Record<string, unknown>> : [];
    (out[no] ||= []).push(
      <div className="step-detail-block" key={step}>
        <div className="step-detail-desc">{String(entry.description || step)}
          {entry.input_count != null ? ` — ${entry.input_count}건 → ${entry.output_count}건` : ""}</div>
        {excluded.length > 0 && (
          <div className="step-detail-list">제외: {excluded.join(", ")}</div>
        )}
        {notSelected.length > 0 && (
          <div className="step-detail-list">
            미선정: {notSelected.slice(0, 12).map((x) => `${x.name}(${Number(x.score ?? 0).toFixed(3)})`).join(", ")}
            {notSelected.length > 12 ? ` 외 ${notSelected.length - 12}곳` : ""}
          </div>
        )}
      </div>,
    );
  }
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, <>{v}</>]));
}

// ── 공용: 진행 카드 ────────────────────────────────────────────────
const STATE_KO = { done: "완료", now: "진행 중", wait: "대기" } as const;

function ProgressCard({ running, steps, seenAt, startedAt, note, titles = [], details }: {
  running: boolean; steps: string[]; seenAt: Record<string, number>;
  startedAt: number; note: string; titles?: string[];
  /** 단계 번호(1부터) → 펼침 상세. 완료 후 filter_log 로 채운다 — 행을 누르면
      그 단계가 무엇을 걸렀는지 엑셀 조서처럼 보인다 (사용자 요청) */
  details?: Record<number, ReactNode>;
}) {
  const [openStep, setOpenStep] = useState<number | null>(null);
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  if (!running && steps.length === 0) return null;

  // 캐시로 건너뛴 단계도 이름을 갖고 자리를 지킨다. 기본 이름은 탭마다 다르므로
  // (사업내용 AI 6단계 / 산업분류 4단계) 호출측이 준다.
  const rows = buildStepRows(steps, seenAt, running, tick, titles);
  // 하트비트를 포함한 마지막 수신 시각 — 멈춘 것처럼 보일 때 판단 근거가 된다
  const lastAt = steps.reduce((mx, s) => Math.max(mx, seenAt[s] || 0), 0);
  const quietSec = running && lastAt ? Math.round((tick - lastAt) / 1000) : 0;
  const extra = steps.filter((s) => !STEP_RE.test(s) && !HEARTBEAT_RE.test(s));

  return (
    <section className="lab-card lab-progress">
      <div className="lab-progress-head">
        {running ? "진행 중" : `진행 로그 (${rows.length}단계)`}
        {running && <span className="lab-timer">{elapsedLabel(startedAt)} · {note}</span>}
      </div>
      {rows.length > 0 && (
        <div className="lab-tablewrap">
          <table className="lab-table lab-steptable">
            <thead>
              <tr><th>단계</th><th>진행 내용</th><th>상태</th><th className="n">소요</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const det = details?.[r.no];
                return (
                  <Fragment key={r.no}>
                    <tr className={`step-${r.state}${det ? " step-expandable" : ""}`}
                        onClick={det ? () => setOpenStep(openStep === r.no ? null : r.no) : undefined}>
                      <td className="mono">{r.no}/{rows.length}</td>
                      <td>{r.text}{det ? <span className="step-caret">{openStep === r.no ? " ▾" : " ▸"}</span> : null}</td>
                      <td><span className={`step-badge ${r.state}`}>{STATE_KO[r.state]}</span></td>
                      <td className="n">{r.secs != null ? `${r.secs}초` : "–"}</td>
                    </tr>
                    {det && openStep === r.no && (
                      <tr className="step-detail"><td colSpan={4}>{det}</td></tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {running && quietSec >= 60 && (
        <p className="lab-desc" style={{ marginTop: 8 }}>
          마지막 갱신 {quietSec}초 전 — 오래 걸리는 단계입니다. 창을 닫아도 진행은 계속됩니다.
        </p>
      )}
      {extra.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="lab-desc" style={{ cursor: "pointer" }}>상세 로그 ({extra.length}건)</summary>
          <div className="lab-steps" style={{ marginTop: 6 }}>
            {extra.map((s, i) => (
              <div key={i} className="lab-step"><span className="dot" /><span>{s}</span></div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function useSteps(persistKey?: string):
    [string[], (m: string) => void, () => void, Record<string, number>] {
  // persistKey 를 주면 페이지를 나갔다 와도 진행 로그가 유지된다
  const local = useState<string[]>([]);
  const persisted = useLabState<string[]>(persistKey || "steps.unused", []);
  const [steps, setSteps] = persistKey ? persisted : local;
  // 문구별 최초 수신 시각 — 단계별 소요시간의 근거. 로그와 같은 수명으로 둔다.
  const [seenAt, setSeenAt] = useLabState<Record<string, number>>(
    persistKey ? `${persistKey}.at` : "steps.unused.at", {});
  const push = (m: string) => {
    setSteps((prev) => (prev[prev.length - 1] === m ? prev : [...prev, m].slice(-40)));
    setSeenAt((prev) => (prev[m] ? prev : { ...prev, [m]: Date.now() }));
  };
  return [steps, push, () => { setSteps([]); setSeenAt({}); }, seenAt];
}

/** 언마운트에도 유지되는 dedup Set — 조서 partial 재기록 방지용. */
function usePersistentSet(key: string): Set<string> {
  const ref = useRef<Set<string> | null>(null);
  if (!ref.current) {
    ref.current = labGet<Set<string>>(key) || new Set();
    labSet(key, ref.current);
  }
  return ref.current;
}

// ── ① 사업내용 AI ─────────────────────────────────────────────────
interface PeerRow {
  name?: string;
  stock_code?: string;
  ticker?: string;
  market?: string;
  score?: number;
  composite?: { total?: number };
  market_cap?: number;
  revenue?: number;
  dart_url?: string;
  sec_url?: string;
  [k: string]: unknown;
}

interface PeerResult {
  peers?: PeerRow[];
  peer_count?: number;
  recall_count?: number;
  elapsed_seconds?: number;
  keywords?: { primary?: string[]; secondary?: string[] };
  [k: string]: unknown;
}

function PeerAiTab() {
  const [business, setBusiness] = useLabState("pa.business", "");
  const [mkKr, setMkKr] = useLabState("pa.mkKr", true);
  const [mkUs, setMkUs] = useLabState("pa.mkUs", false);
  const [topK, setTopK] = useLabState("pa.topK", 10);
  const [running, setRunning] = useLabState("pa.running", false);
  const [startedAt, setStartedAt] = useLabState("pa.startedAt", 0);
  const [steps, pushStep, resetSteps, stepAt] = useSteps("pa.steps");
  const [result, setResult] = useLabState<PeerResult | null>("pa.result", null);
  const [err, setErr] = useLabState("pa.err", "");
  const [fileBusy, setFileBusy] = useState("");
  const [fileUrls, setFileUrls] = useLabState<{ label: string; url: string; kind?: "pdf" | "excel" }[]>("pa.fileUrls", []);
  const openPreview = useContext(PeerPreviewCtx);
  const pollRef = useRef<PollHandle | null>(null);
  // 중단 버튼이 서버에 보낼 task_id. 폴링만 끊으면 서버 작업은 계속 돌아
  // 결과·스냅샷이 저장되므로, 중단은 반드시 서버까지 가야 한다.
  const taskRef = useRef<string>("");
  // 실행 세대 — stop() 이 아직 없는 폴링을 끊을 수 없으므로, run() 이
  // peerStart 응답을 기다리는 중에 중단하면 await 뒤 폴링이 새로 걸린다.
  const runIdRef = useRef(0);
  const sheet = useLiveSheet("pa.sheet");
  const written = usePersistentSet("pa.written");
  // 사이드바 완료 점(pa.done) — 보고 있으면 알림이 필요 없다. 페이지가 떠 있는
  // 동안 완료가 도착해도(running→false, result 갱신) 이 effect 가 바로 끈다.
  useEffect(() => {
    if (!running) labSet("pa.done", false);
  }, [running, result]);
  // 언마운트해도 폴링을 죽이지 않는다 — 돌아오면 진행 화면이 복원된다

  // 딥링크(?tab=ai&rid=) — 영구 스냅샷에서 그날의 결과를 즉시 복원
  const { search, completed } = useContext(PeerNavigationCtx);
  useEffect(() => {
    const p = new URLSearchParams(search);
    const rid = p.get("rid");
    if (!rid || p.get("tab") !== "ai" || running) return;
    if ((result as { result_id?: string } | null)?.result_id === rid) return;
    void peerResult<PeerResult>(rid).then((r) => {
      if (!r) return;
      sheet.reset();
      sheet.append([{ kind: "title", cells: ["유사기업 검토 조서 — 저장된 조회 복원"] }]);
      sheet.append(peerFinalLines(r));
      setResult(r);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 복원한 조회의 '사업의 내용'도 입력칸에 채운다 — 스냅샷에는 결과만 있고 입력은 조회 이력(label, 원문 2,000자)에
  // 남아 있다. 이력은 서버 목록이 늦게 올 수 있어 목록이 바뀔 때마다 한 번 더 보고, rid 마다 한 번만 채운다.
  const recentList = usePeerRecent();
  const filledRid = useRef<string | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(search);
    const rid = p.get("rid");
    if (!rid || p.get("tab") !== "ai" || running || filledRid.current === rid) return;
    const fromResult = (result as { business_text?: string } | null)?.business_text;
    const text = (fromResult || recentList.find((x) => x.rid === rid && x.tab === "ai")?.label || "").trim();
    if (!text) return;
    filledRid.current = rid;
    if (text !== business.trim()) setBusiness(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, recentList, result, running]);

  const run = async () => {
    const markets = [...(mkKr ? ["kr"] : []), ...(mkUs ? ["us"] : [])];
    if (business.trim().length < 10) { setErr("사업의 내용을 10자 이상 입력하세요."); return; }
    if (!markets.length) { setErr("탐색 시장을 하나 이상 선택하세요."); return; }
    setErr(""); setResult(null); setFileUrls([]); resetSteps();
    sheet.reset(); written.clear();
    const t0 = Date.now();
    const myRun = ++runIdRef.current;
    const stale = () => runIdRef.current !== myRun;
    setRunning(true); setStartedAt(t0);
    labHeartbeat("pa", t0, (line) => pushStep(line));
    pushStep("유사기업 선정 시작 — 전 상장사 사업보고서 키워드 매칭");
    sheet.append([{ kind: "title", cells: ["유사기업 검토 조서 — 사업내용 AI 선정"] },
                  { kind: "note", cells: [`입력 사업내용: ${business.trim().slice(0, 100)}`] }]);
    try {
      const taskId = await peerStart({
        business_text: business.trim(), markets, top_k: topK,
      });
      if (stale()) {
        // 중단 뒤에 응답이 왔다 — 서버에도 알려 결과가 저장되지 않게 한다
        void peerCancel(taskId).catch(() => undefined);
        return;
      }
      taskRef.current = taskId;
      pollRef.current = pollTask<PeerResult>({
        statusPath: peerStatusPath, taskId,
        intervalMs: 2500, timeoutMs: 12 * 60_000,
        onStep: (step, partial) => {
          pushStep(step);
          // 완료된 단계의 부분 결과 → 조서 행으로 즉시 기록 (officejs 라이브 시트와 동일)
          sheet.append(peerPartialLines(partial, markets, written));
        },
        resolve: (r) => {
          sheet.append(peerFinalLines(r));
          labStopHeartbeat("pa");
          labSet("pa.done", true);   // 사이드바 완료 점 — 페이지를 보고 있으면 effect 가 바로 끈다
          setResult(r); setRunning(false);
          // 결과 화면의 영구 주소 — 문서함·공유에서 이 URL 로 그날의 결과 복원
          const rid = (r as { result_id?: string }).result_id;
          if (rid) {
            completed("ai", rid);
            recordPeerRecent({
              rid, tab: "ai", label: business.trim(),
              count: Number(r.peer_count) || (r.peers || []).length,
            });
          }
        },
        reject: (e) => { labStopHeartbeat("pa"); labSet("pa.done", true); setErr(e.message); setRunning(false); },
      });
    } catch (e) {
      if (stale()) return;
      setErr(e instanceof Error ? e.message : String(e));
      setRunning(false);
    }
  };

  /** 실행 중단 — 폴링을 끊고 서버에도 알린다(중단분은 저장하지 않는다). */
  const stop = () => {
    runIdRef.current += 1;          // 진행 중인 run() 의 이후 단계를 모두 무효화
    pollRef.current?.cancel();
    pollRef.current = null;
    labStopHeartbeat("pa");
    labSet("pa.done", true);
    setRunning(false);
    pushStep("사용자가 실행을 중단했습니다.");
    const tid = taskRef.current;
    taskRef.current = "";
    if (tid) void peerCancel(tid).catch(() => undefined);
  };

  const makeFile = async (kind: "excel" | "pdf") => {
    if (!result) return;
    setFileBusy(kind); setErr("");
    try {
      const payload = { business_text: business.trim(), result };
      const url = kind === "excel" ? await peerExcel(payload) : await peerPdf(payload);
      setFileUrls((prev) => [...prev, { label: kind === "excel" ? "엑셀 파일 ↗" : "선정 보고서 PDF ↗", url, kind }]);
      recordLabFile({
        url, kind: kind === "excel" ? "excel" : "report", feature: "유사기업",
        page: `/peer?tab=ai${(result as { result_id?: string } | null)?.result_id ? `&rid=${(result as { result_id?: string }).result_id}` : ""}`,
        company: "",
        label: decodeURIComponent((url.split("?")[0].split("/").pop() || "")) || `유사기업_${kind}.${kind === "excel" ? "xlsx" : "pdf"}`,
      });
      // 다운로드로 튕기지 않는다 — 우측 미리보기 패널 (사용자 지정, 버핏과 동일 UX)
      openPreview({ url, kind, title: kind === "excel" ? "유사기업 선정 엑셀" : "유사기업 선정 보고서",
                    sub: business.trim().slice(0, 60) });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setFileBusy("");
    }
  };

  const scoreOf = (p: PeerRow) => p.composite?.total ?? p.score ?? null;

  return (
    <>
      <section className="lab-card">
        <h2>평가대상 사업내용으로 유사기업 선정</h2>
        <p className="lab-desc">
          사업의 내용을 입력하면 전체 상장사의 사업보고서에서 키워드를 매칭하고
          정성 제외·정량 스코어링을 거쳐 최종 유사기업을 선정합니다 (2~7분).
        </p>
        <div className="lab-row" style={{ marginBottom: 10 }}>
          <label className="lab-check"><input type="checkbox" checked={mkKr} onChange={(e) => setMkKr(e.target.checked)} disabled={running} /> 국내 (KRX)</label>
          <label className="lab-check"><input type="checkbox" checked={mkUs} onChange={(e) => setMkUs(e.target.checked)} disabled={running} /> 미국 (Nasdaq·NYSE)</label>
        </div>
        <div className="lab-row">
          <div className="lab-field grow" style={{ flexBasis: "100%" }}>
            평가대상 사업의 내용 *
            <textarea value={business} disabled={running}
              placeholder="예: 골관절염 유전자치료제(인보사) 개발 및 글로벌 임상 추진, 조직재생 특화 바이오"
              onChange={(e) => setBusiness(e.target.value)} />
          </div>
        </div>
        <div className="lab-row" style={{ marginTop: 10 }}>
          <div className="lab-field">선정 기업 수
            <input type="number" min={3} max={30} value={topK} disabled={running}
              onChange={(e) => setTopK(Math.max(3, Math.min(30, Number(e.target.value) || 10)))} style={{ width: 90 }} />
          </div>
          <Button variant={running ? "del" : "primary"} onClick={running ? stop : () => void run()}>
            {running ? "실행 중단" : "유사기업 선정"}
          </Button>
        </div>
        {err && <p className="lab-error" role="alert" style={{ marginTop: 10 }}>{err}</p>}
      </section>

      {result && (
        <section className="lab-card">
          <h2>선정 결과 — {result.peer_count ?? result.peers?.length ?? 0}개사</h2>
          <p className="lab-desc">
            후보 {fmtNum(result.recall_count)}개사 검토 · {fmtNum(result.elapsed_seconds)}초 소요
          </p>
          {(result.keywords?.primary?.length || result.keywords?.secondary?.length) ? (
            <div className="lab-chips" style={{ marginBottom: 12 }}>
              {(result.keywords?.primary || []).map((k) => <span className="lab-chip" key={k}>{k}</span>)}
              {(result.keywords?.secondary || []).map((k) => <span className="lab-chip soft" key={k}>{k}</span>)}
            </div>
          ) : null}
          <div className="lab-tablewrap">
            <table className="lab-table">
              <thead>
                <tr>
                  <th>#</th><th>회사명</th><th>코드</th><th className="n">점수</th>
                  <th className="n">시가총액</th><th>비고</th><th>링크</th>
                </tr>
              </thead>
              <tbody>
                {(result.peers || []).map((p, i) => {
                  const sc = scoreOf(p);
                  return (
                    <tr key={`${p.stock_code || p.ticker || i}`}>
                      <td>{i + 1}</td>
                      <td>
                        <span className="lab-co">
                          <CompanyLogo symbol={p.ticker || p.stock_code} name={p.name} size="sm" />
                          {String(p.name || "")}{p.market === "us" ? " 🇺🇸" : ""}
                        </span>
                      </td>
                      <td className="mono">{String(p.ticker || p.stock_code || "")}</td>
                      <td className={`n ${sc != null && sc >= 0.6 ? "score-hi" : sc != null && sc >= 0.4 ? "score-md" : "score-lo"}`}>
                        {sc != null ? sc.toFixed(3) : "–"}
                      </td>
                      <td className="n">{fmtNum(p.market_cap)}</td>
                      <td className="peer-remark">
                        {((p as { remarks?: { warnings?: string[] } }).remarks?.warnings || []).join(" · ")}
                      </td>
                      <td>
                        {p.dart_url ? <a href={String(p.dart_url)} target="_blank" rel="noreferrer">DART</a> : null}
                        {p.sec_url ? <a href={String(p.sec_url)} target="_blank" rel="noreferrer">10-K</a> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="lab-actions">
            <RelativeLink rows={result.peers || []} source="유사기업 AI 선정" />
            <Button variant="default" disabled={!!fileBusy} onClick={() => void makeFile("excel")}>
              {fileBusy === "excel" ? "생성 중…" : "엑셀 파일로"}
            </Button>
            <Button variant="default" disabled={!!fileBusy} onClick={() => void makeFile("pdf")}>
              {fileBusy === "pdf" ? "생성 중…" : "PDF 보고서"}
            </Button>
            {fileUrls.map((f) => (
              <button className="lab-chip" type="button" key={f.url}
                onClick={() => openPreview({ url: f.url, kind: f.kind || (f.label.includes("엑셀") ? "excel" : "pdf"),
                                             title: f.label.replace(" ↗", "") })}>
                {f.label.replace("↗", "미리보기")}
              </button>
            ))}
          </div>
        </section>
      )}
      {/* 결과가 나오면 진행 카드는 아래로 — 사용자가 먼저 볼 것은 결과표다 */}
      <ProgressCard running={running} steps={steps} seenAt={stepAt} startedAt={startedAt} note="2~7분 걸립니다"
        titles={PEER_STEP_TITLES} details={aiStepDetails(result)} />
      <LiveSheet lines={sheet.lines} running={running} />
    </>
  );
}

// ── ② 산업분류(KSIC) ──────────────────────────────────────────────
interface StageData {
  count?: number;
  passed_count?: number;
  // 백엔드가 단계마다 목록 필드명이 다르다 — 1차 universe · 2/4차 rows · 3차 candidates
  companies?: Record<string, unknown>[];
  universe?: Record<string, unknown>[];
  rows?: Record<string, unknown>[];
  candidates?: Record<string, unknown>[];
  [k: string]: unknown;
}

/** 단계별 회사 목록 — 필드명이 제각각이라 여기서 한 번에 흡수한다 */
function stageCompanies(stage: StageData): Record<string, unknown>[] {
  return stage.companies || stage.universe || stage.rows || stage.candidates || [];
}

interface IndustryResult {
  stage1?: StageData; stage2?: StageData; stage3?: StageData; stage4?: StageData;
  /** 백엔드는 배열로 준다 — 과거 {companies} 형태 결과(rid 복원)도 함께 흡수 */
  final?: Record<string, unknown>[] | { companies?: Record<string, unknown>[]; count?: number };
  [k: string]: unknown;
}

/** 산업분류 스냅샷(meta) → 입력칸 값. 값이 없거나 형식이 다르면 그 칸은 건드리지 않는다(null/undefined) */
export function industryRestoreInputs(result: IndustryResult): {
  business?: string; chosen?: string[]; targetSymbol?: string;
  years?: number; minMargin?: number; minMonths?: number; minRevPct?: number;
} {
  const meta = (result.meta && typeof result.meta === "object" ? result.meta : {}) as Record<string, unknown>;
  const criteria = (meta.criteria && typeof meta.criteria === "object" ? meta.criteria : {}) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
  const names = Array.isArray(meta.industry_names) ? meta.industry_names.filter((n): n is string => typeof n === "string" && n.trim() !== "") : undefined;
  const margin = num(criteria.min_avg_operating_margin);
  return {
    business: typeof meta.business_text === "string" ? meta.business_text : undefined,
    chosen: names && names.length ? names : undefined,
    targetSymbol: typeof meta.target_symbol === "string" ? meta.target_symbol : undefined,
    years: num(criteria.years),
    minMargin: margin == null ? undefined : Math.round(margin * 1000) / 10,   // 0.15 → 15 (%)
    minMonths: num(criteria.min_months_listed),
    minRevPct: num(criteria.min_revenue_pct),
  };
}

function finalCompanies(result: IndustryResult): Record<string, unknown>[] | undefined {
  const f = result.final;
  if (Array.isArray(f)) return f;
  return f?.companies;
}

const STAGE_LABELS: [string, string][] = [
  ["stage1", "1차 · 산업분류 모집단"],
  ["stage2", "2차 · 재무 요건"],
  ["stage3", "3차 · 사업 유사성 (매출구조)"],
  ["stage4", "4차 · 일반 요건 (상장기간·관리종목 등)"],
];

function IndustryTab() {
  const [business, setBusiness] = useLabState("it.business", "");
  const [suggests, setSuggests] = useLabState<IndustrySuggestion[]>("it.suggests", []);
  const [chosen, setChosen] = useLabState<string[]>("it.chosen", []);
  const [suggestBusy, setSuggestBusy] = useState(false);
  // 회사로 산업 조회 — 상장사명/종목코드 자동완성 → 선택 시 그 회사의 분류를 추가
  const [companyQ, setCompanyQ] = useState("");
  const [companyHits, setCompanyHits] = useState<{ symbol: string; name: string }[]>([]);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [targetSymbol, setTargetSymbol] = useState("");
  // 산업분류 직접 추가 — KSIC 카탈로그(상장사 실제 분류 전수) 자동완성
  const [catalog, setCatalog] = useState<string[]>([]);
  const [indQ, setIndQ] = useState("");
  const [years, setYears] = useState(2);
  const [minMargin, setMinMargin] = useState(15);
  const [minMonths, setMinMonths] = useState(6);
  const [minRevPct, setMinRevPct] = useState(10);
  const [running, setRunning] = useLabState("it.running", false);
  const [startedAt, setStartedAt] = useLabState("it.startedAt", 0);
  const [steps, pushStep, resetSteps, stepAt] = useSteps("it.steps");
  const [result, setResult] = useLabState<IndustryResult | null>("it.result", null);
  const [err, setErr] = useLabState("it.err", "");
  const openPreview = useContext(PeerPreviewCtx);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useLabState("it.pdfUrl", "");
  const pollRef = useRef<PollHandle | null>(null);
  const sheet = useLiveSheet("it.sheet");
  const stageWritten = usePersistentSet("it.written");
  // 사이드바 완료 점(it.done) — 페이지를 보고 있으면 완료 즉시 끈다
  useEffect(() => {
    if (!running) labSet("it.done", false);
  }, [running, result]);

  // 딥링크(?tab=industry&rid=) — 영구 스냅샷 복원
  const { search, completed } = useContext(PeerNavigationCtx);
  useEffect(() => {
    const p = new URLSearchParams(search);
    const rid = p.get("rid");
    if (!rid || p.get("tab") !== "industry" || running) return;
    if ((result as { result_id?: string } | null)?.result_id === rid) return;
    void peerResult<IndustryResult>(rid).then((r) => {
      if (!r) return;
      sheet.reset();
      stageWritten.clear();
      sheet.append([{ kind: "title", cells: ["산업분류 검토 조서 — 저장된 조회 복원"] }]);
      sheet.append(industryStageLines(r as Record<string, unknown>, stageWritten));
      setResult(r);
      // 입력값도 그날 조회 그대로 — 스냅샷 meta 에 사업의 내용·선택 분류·조건이 남아 있다
      const inputs = industryRestoreInputs(r);
      if (inputs.business != null) setBusiness(inputs.business);
      if (inputs.chosen) setChosen(inputs.chosen);
      if (inputs.targetSymbol != null) setTargetSymbol(inputs.targetSymbol);
      if (inputs.years != null) setYears(inputs.years);
      if (inputs.minMargin != null) setMinMargin(inputs.minMargin);
      if (inputs.minMonths != null) setMinMonths(inputs.minMonths);
      if (inputs.minRevPct != null) setMinRevPct(inputs.minRevPct);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  // 언마운트해도 폴링 유지 (탐색 생존)

  useEffect(() => {
    void industryCatalog().then(setCatalog).catch(() => {});
  }, []);

  // 회사 검색 자동완성 (300ms 디바운스)
  useEffect(() => {
    const kw = companyQ.trim();
    if (kw.length < 2) { setCompanyHits([]); return; }
    const t = setTimeout(() => {
      void searchCompany(kw).then((items) => setCompanyHits(items.slice(0, 8)));
    }, 300);
    return () => clearTimeout(t);
  }, [companyQ]);

  const addChosen = (name: string) => {
    const n = name.trim();
    if (!n) return;
    setChosen((prev) => (prev.includes(n) ? prev : [...prev, n]));
  };

  // 회사 선택 → 그 회사가 속한 산업분류를 조회해 자동 추가
  const pickCompany = async (hit: { symbol: string; name: string }) => {
    const code = hit.symbol.replace(/^[A-Za-z]+:/, "");
    setCompanyQ(hit.name);
    setCompanyHits([]);
    setTargetSymbol(code);
    setCompanyBusy(true);
    setErr("");
    try {
      const items = await industrySuggest({ business_text: "", target_symbol: code, max_suggestions: 5 });
      if (items.length) {
        addChosen(items[0].industry_name);   // 최우선 후보 = 그 회사의 분류
        setSuggests((prev) => {
          const seen = new Set(prev.map((x) => x.industry_name));
          return [...prev, ...items.filter((x) => !seen.has(x.industry_name))];
        });
      } else {
        setErr(`${hit.name} 의 산업분류를 찾지 못했습니다.`);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCompanyBusy(false);
    }
  };

  const suggest = async () => {
    if (business.trim().length < 10) { setErr("사업의 내용을 10자 이상 입력하세요."); return; }
    setErr(""); setSuggestBusy(true);
    try {
      const items = await industrySuggest({
        business_text: business.trim(),
        target_symbol: targetSymbol || undefined,
      });
      setSuggests(items);
      items.slice(0, 5).forEach((x) => addChosen(x.industry_name));   // 상위 5개 자동 선택
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSuggestBusy(false);
    }
  };

  const run = async () => {
    if (!chosen.length) { setErr("산업분류를 하나 이상 선택하세요 (추천받기 또는 직접 추가)."); return; }
    setErr(""); setResult(null); setPdfUrl(""); resetSteps();
    sheet.reset(); stageWritten.clear();
    const t0 = Date.now();
    setRunning(true); setStartedAt(t0);
    labHeartbeat("it", t0, (line) => pushStep(line));
    pushStep(`4단계 스크리닝 시작 — 분류 ${chosen.length}개`);
    sheet.append([{ kind: "title", cells: ["산업분류 유사기업 검토 조서 (KSIC 4단계)"] },
                  { kind: "note", cells: [`모집단 분류: ${chosen.join(", ")}`] }]);
    try {
      const taskId = await industryScreenStart({
        industry_names: chosen,
        base_date: new Date().toISOString().slice(0, 10),   // 필수 — 분석기준일
        business_text: business.trim(),
        target_symbol: targetSymbol || undefined,
        years,
        min_avg_operating_margin: minMargin / 100,
        min_months_listed: minMonths,
        min_revenue_pct: minRevPct,
        require_positive_growth: true,
        require_december_fye: true,
        require_clean_audit: true,
      });
      pollRef.current = pollTask<IndustryResult>({
        statusPath: peerStatusPath, taskId,   // 산업 스크리닝도 peer/status 태스크 스토어 공용
        intervalMs: 2500, timeoutMs: 30 * 60_000,
        onStep: (step, partial) => {
          pushStep(step);
          if (partial && typeof partial === "object") {
            sheet.append(industryStageLines(partial as Record<string, unknown>, stageWritten));
            setResult((prev) => ({ ...(prev || {}), ...(partial as IndustryResult) }));
          }
        },
        resolve: (r) => {
          sheet.append(industryStageLines(r as Record<string, unknown>, stageWritten));
          labStopHeartbeat("it");
          labSet("it.done", true);   // 사이드바 완료 점 — 페이지를 보고 있으면 effect 가 바로 끈다
          setResult(r); setRunning(false);
          const rid = (r as { result_id?: string }).result_id;
          if (rid) {
            completed("industry", rid);
            recordPeerRecent({
              rid, tab: "industry", label: chosen.join(", "),
              count: finalCompanies(r)?.length,
            });
          }
        },
        reject: (e) => { labStopHeartbeat("it"); labSet("it.done", true); setErr(e.message); setRunning(false); },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setRunning(false);
    }
  };

  const [xlsxBusy, setXlsxBusy] = useState(false);
  const downloadExcel = async () => {
    if (!result) return;
    setXlsxBusy(true); setErr("");
    try {
      const saved = await industryExcel({ result });
      recordLabFile({
        url: saved.url, kind: "excel", feature: "산업분류",
        page: `/peer?tab=industry${(result as { result_id?: string } | null)?.result_id ? `&rid=${(result as { result_id?: string }).result_id}` : ""}`,
        company: "", label: saved.filename,
      });
      if (saved.contentBase64) downloadBase64(saved.contentBase64, saved.filename, XLSX_MIME);
      else window.open(saved.url, "_blank", "noopener");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setXlsxBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setPdfBusy(true); setErr("");
    try {
      const url = await industryPdf({ result });
      setPdfUrl(url);
      recordLabFile({
        url, kind: "report", feature: "산업분류",
        page: `/peer?tab=industry${(result as { result_id?: string } | null)?.result_id ? `&rid=${(result as { result_id?: string }).result_id}` : ""}`,
        company: "",
        label: decodeURIComponent((url.split("?")[0].split("/").pop() || "")) || "산업분류_보고서.pdf",
      });
      openPreview({ url, kind: "pdf", title: "산업분류 선정 보고서" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfBusy(false);
    }
  };

  const indMatches = indQ.trim().length >= 1
    ? catalog.filter((n) => n.includes(indQ.trim())).slice(0, 12)
    : [];

  return (
    <>
      <section className="lab-card">
        <h2>산업분류(KSIC) 기반 4단계 스크리닝</h2>
        <p className="lab-desc">
          증권신고서 표준 방식 — ① 산업분류 모집단 → ② 재무 요건 → ③ 사업 유사성(매출구조) →
          ④ 일반 요건 순으로 걸러냅니다. 사업내용으로 추천받거나, 산업분류를 직접 추가하거나,
          상장사를 검색해 그 회사의 분류를 가져올 수 있습니다.
        </p>
        <div className="lab-row">
          <div className="lab-field grow" style={{ flexBasis: "100%" }}>
            평가대상 사업의 내용
            <textarea value={business} disabled={running}
              placeholder="예: 반도체 검사장비 제조 및 판매"
              onChange={(e) => setBusiness(e.target.value)} />
          </div>
        </div>
        <div className="lab-row" style={{ marginTop: 10 }}>
          <Button variant="default" disabled={suggestBusy || running} onClick={() => void suggest()}>
            {suggestBusy ? "추천 중…" : "사업내용으로 추천받기"}
          </Button>
          <div className="lab-field grow lab-suggest-wrap">
            산업분류 직접 추가
            <input value={indQ} disabled={running} placeholder="분류명 입력 (예: 반도체 제조업)"
              onChange={(e) => setIndQ(e.target.value)} />
            {indMatches.length > 0 && (
              <div className="lab-suggest">
                {indMatches.map((n) => (
                  <button key={n} type="button" onClick={() => { addChosen(n); setIndQ(""); }}>
                    <span>{n}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="lab-field grow lab-suggest-wrap">
            상장사로 분류 가져오기
            <input value={companyQ} disabled={running || companyBusy}
              placeholder="회사명 또는 종목코드 (예: 삼성전자)"
              onChange={(e) => setCompanyQ(e.target.value)} />
            {companyHits.length > 0 && (
              <div className="lab-suggest">
                {companyHits.map((h) => (
                  <button key={h.symbol} type="button" onClick={() => void pickCompany(h)}>
                    <span className="lab-co">
                      <CompanyLogo symbol={h.symbol} name={h.name} size="sm" />
                      {h.name}
                    </span>
                    <span className="sym">{h.symbol}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {chosen.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="lab-note" style={{ marginBottom: 6 }}>선택된 분류 {chosen.length}개 — 눌러서 제거</div>
            <div className="lab-chips">
              {chosen.map((n) => (
                <button key={n} type="button" className="lab-chip pick on"
                  title="클릭하여 제거"
                  onClick={() => setChosen((prev) => prev.filter((x) => x !== n))}>
                  {n} ✕
                </button>
              ))}
            </div>
          </div>
        )}
        {suggests.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="lab-note" style={{ marginBottom: 6 }}>추천 후보 — 눌러서 추가</div>
            <div className="lab-chips">
              {suggests.filter((sug) => !chosen.includes(sug.industry_name)).map((sug) => (
                <button key={sug.industry_name} type="button" className="lab-chip pick"
                  onClick={() => addChosen(sug.industry_name)}>
                  {sug.industry_name}{typeof sug.listed_count === "number" ? ` (${sug.listed_count})` : ""}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="lab-row" style={{ marginTop: 14 }}>
          <div className="lab-field">검토 연수
            <input type="number" min={1} max={5} value={years} disabled={running} onChange={(e) => setYears(Number(e.target.value) || 2)} style={{ width: 80 }} />
          </div>
          <div className="lab-field">평균 영업이익률 하한 %
            <input type="number" value={minMargin} disabled={running} onChange={(e) => setMinMargin(Number(e.target.value) || 0)} style={{ width: 100 }} />
          </div>
          <div className="lab-field">상장 경과 개월
            <input type="number" value={minMonths} disabled={running} onChange={(e) => setMinMonths(Number(e.target.value) || 0)} style={{ width: 100 }} />
          </div>
          <div className="lab-field">관련 매출비중 하한 %
            <input type="number" value={minRevPct} disabled={running} onChange={(e) => setMinRevPct(Number(e.target.value) || 0)} style={{ width: 110 }} />
          </div>
          <Button variant="primary" disabled={running || !chosen.length} onClick={() => void run()}>
            {running ? "스크리닝 중…" : "스크리닝 시작"}
          </Button>
        </div>
        <p className="lab-note" style={{ marginTop: 8 }}>양(+)의 매출성장률 · 12월 결산 · 감사의견 적정 조건이 기본 적용됩니다.</p>
        {err && <p className="lab-error" role="alert" style={{ marginTop: 10 }}>{err}</p>}
      </section>

      {result && (
        <section className="lab-card">
          <h2>스크리닝 결과{finalCompanies(result) ? ` — 최종 ${finalCompanies(result)!.length}개사` : " (진행 중 부분 결과)"}</h2>
          {STAGE_LABELS.map(([key, label]) => {
            const st = result[key] as StageData | undefined;
            if (!st) return null;
            return <StageSection key={key} stageKey={key} label={label} stage={st} />;
          })}
          {finalCompanies(result)?.length ? (
            <div style={{ marginTop: 12 }}>
              <h2 style={{ marginBottom: 8 }}>최종 선정</h2>
              <CompanyTable companies={finalCompanies(result)!} />
            </div>
          ) : null}
          {result.final ? (
            <div className="lab-actions">
              <RelativeLink rows={finalCompanies(result) || []} source="산업분류 스크리닝" />
              <Button variant="default" disabled={xlsxBusy} onClick={() => void downloadExcel()}>
                {xlsxBusy ? "생성 중…" : "엑셀 파일로"}
              </Button>
              <Button variant="default" disabled={pdfBusy} onClick={() => void downloadPdf()}>
                {pdfBusy ? "생성 중…" : "PDF 보고서"}
              </Button>
              {pdfUrl && <a className="lab-chip" href={pdfUrl} target="_blank" rel="noreferrer">조서 다시 열기 ↗</a>}
            </div>
          ) : null}
        </section>
      )}
      {/* 결과가 나오면 진행 카드는 아래로 — 사용자가 먼저 볼 것은 결과표다 */}
      <ProgressCard running={running} steps={steps} seenAt={stepAt} startedAt={startedAt} note="분류 수에 따라 수 분 걸립니다" />
      <LiveSheet lines={sheet.lines} running={running} />
    </>
  );
}

/** 단계 헤더 개수 — 판정이 있는 단계(2·4차)는 '검토 M개사 중 N개사 통과', 1차는 모집단·제외, 3차는 통과·제외 */
export function stageSummary(stageKey: string, stage: StageData): string {
  const companies = stageCompanies(stage);
  const judged = companies.some((c) => typeof c.selected === "boolean");
  if (judged) {
    const passed = stage.passed_count ?? companies.filter((c) => c.selected === true).length;
    return `${companies.length}개사 중 ${passed}개사 통과`;
  }
  if (stageKey === "stage1") {
    const ex = Array.isArray(stage.excluded) ? stage.excluded.length : 0;
    return `모집단 ${stage.count ?? companies.length}개사${ex ? ` · 제외 ${ex}개사` : ""}`;
  }
  if (stageKey === "stage3") {
    const ex = typeof stage.excluded_count === "number" ? stage.excluded_count : Array.isArray(stage.excluded) ? stage.excluded.length : 0;
    return `통과 ${stage.passed_count ?? companies.length}개사${ex ? ` · 제외 ${ex}개사` : ""}`;
  }
  const n = stage.passed_count ?? stage.count ?? companies.length;
  return typeof n === "number" ? `${n}개사` : "";
}

/** 판정이 있는 행은 통과 먼저, 같은 판정 안에서는 원래 순서 */
function passedFirst(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((r, i) => [r, i] as const)
    .sort((a, b) => (a[0].selected === b[0].selected ? a[1] - b[1] : a[0].selected === true ? -1 : 1))
    .map(([r]) => r);
}

function StageSection({ stageKey, label, stage }: { stageKey: string; label: string; stage: StageData }) {
  const [open, setOpen] = useState(false);
  const companies = stageCompanies(stage);
  const excluded = Array.isArray(stage.excluded) ? (stage.excluded as Record<string, unknown>[]) : [];
  return (
    <div className="lab-acc">
      <button type="button" onClick={() => setOpen(!open)}>
        <span>{label}</span>
        <span className="acc-chip">{stageSummary(stageKey, stage)} {open ? "접기" : "펼치기"}</span>
      </button>
      {open && (
        <div className="acc-body">
          {stageKey === "stage2" && companies.length ? <FinancialStageTable stage={stage} rows={companies} />
            : stageKey === "stage4" && companies.length ? <GeneralStageTable rows={companies} />
            : stageKey === "stage3" ? <BusinessStageTable rows={companies} excluded={excluded} />
            : companies.length ? <CompanyTable companies={companies} /> : "회사 목록 없음"}
          {stageKey === "stage1" && excluded.length ? (
            <details className="stage-excluded">
              <summary>모집단에서 제외된 {excluded.length}개사 (사유 보기)</summary>
              <CompanyTable companies={excluded} />
            </details>
          ) : null}
        </div>
      )}
    </div>
  );
}

const CHECK_MARK = (v: unknown) => (v === true ? <span className="chk ok">○</span> : v === false ? <span className="chk ng">×</span> : <span className="chk">–</span>);
const pct = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "–");
const eok = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? `${Math.round(v / 1e8).toLocaleString()}억` : "–");
function Verdict({ row }: { row: Record<string, unknown> }) {
  return row.selected === true ? <span className="verdict ok">통과</span> : <span className="verdict ng">제외</span>;
}
function DartLink({ row }: { row: Record<string, unknown> }) {
  return typeof row.dart_url === "string" ? <a href={row.dart_url} target="_blank" rel="noreferrer">원문 ↗</a> : <>–</>;
}

/** 2차 재무 요건 — 연도별 매출·영업이익률·성장률, 평균 영업이익률, 감사의견, 기준별 충족(○/×), 판정·사유 */
function FinancialStageTable({ stage, rows }: { stage: StageData; rows: Record<string, unknown>[] }) {
  const criteria = (stage.criteria && typeof stage.criteria === "object" ? stage.criteria : {}) as Record<string, unknown>;
  const years = Array.isArray(criteria.year_list) ? (criteria.year_list as number[]) : [];
  const minMargin = typeof criteria.min_avg_operating_margin === "number" ? criteria.min_avg_operating_margin : null;
  const finOf = (row: Record<string, unknown>, y: number) =>
    ((Array.isArray(row.financials) ? row.financials : []) as Record<string, unknown>[]).find((f) => Number(f.year) === y) || {};
  return (
    <>
      <p className="lab-note stage-criteria">
        기준: {years.length ? `${years.join("·")}년` : "검토 연도"} 영업이익·당기순이익 흑자 · 매출 성장(+){minMargin != null ? ` · 평균 영업이익률 ≥ ${(minMargin * 100).toFixed(1)}%` : ""}
        {criteria.require_clean_audit ? " · 감사의견 적정" : ""}{criteria.require_december_fye ? " · 12월 결산" : ""}
      </p>
      <div className="lab-tablewrap" style={{ marginTop: 6 }}>
        <table className="lab-table stage-table">
          <thead>
            <tr>
              <th>판정</th><th>회사명</th>
              <th className="c">흑자</th><th className="c">매출 성장</th><th className="c">이익률</th><th className="c">감사 적정</th><th className="c">12월 결산</th>
              <th>제외 사유</th>
              <th className="n">평균 영업이익률</th>
              {years.map((y) => <th key={y} className="n">{y}<div className="sub">매출 · 영업이익률 · 성장률</div></th>)}
              <th>감사의견</th><th>원문</th>
            </tr>
          </thead>
          <tbody>
            {passedFirst(rows).map((row, i) => {
              const checks = (row.checks && typeof row.checks === "object" ? row.checks : {}) as Record<string, unknown>;
              return (
                <tr key={i} className={row.selected === true ? "pass" : "fail"}>
                  <td><Verdict row={row} /></td>
                  <td className="name">{String(row.name ?? "–")}<div className="sub">{String(row.market ?? "")} · {String(row.symbol ?? "")}</div></td>
                  <td className="c">{CHECK_MARK(checks.profit)}</td><td className="c">{CHECK_MARK(checks.growth)}</td>
                  <td className="c">{CHECK_MARK(checks.margin)}</td><td className="c">{CHECK_MARK(checks.audit)}</td>
                  <td className="c">{CHECK_MARK(checks.fiscal_year_end)}</td>
                  <td className="reason">{row.selected === true ? "—" : String(row.reason ?? "")}</td>
                  <td className="n">{pct(row.avg_operating_margin)}</td>
                  {years.map((y) => {
                    const f = finOf(row, y);
                    return <td key={y} className="n fin">{eok(f.sales)}<div className="sub">{pct(f.operating_margin)} · {pct(f.revenue_growth)}</div></td>;
                  })}
                  <td>{String(row.audit_opinion ?? "–")}</td>
                  <td><DartLink row={row} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** 3차 사업 유사성 — 통과 회사의 관련 매출비중·매출구조, 제외 회사와 사유 */
function BusinessStageTable({ rows, excluded }: { rows: Record<string, unknown>[]; excluded: Record<string, unknown>[] }) {
  const all: Record<string, unknown>[] = [...rows.map((r) => ({ ...r, selected: true })), ...excluded.map((r) => ({ ...r, selected: false }))];
  if (!all.length) return <>회사 목록 없음</>;
  return (
    <div className="lab-tablewrap" style={{ marginTop: 6 }}>
      <table className="lab-table stage-table">
        <thead><tr><th>판정</th><th>회사명</th><th>산업분류</th><th>매출구조 · 사유</th><th>원문</th></tr></thead>
        <tbody>
          {all.map((row, i) => (
            <tr key={i} className={row.selected ? "pass" : "fail"}>
              <td><Verdict row={row} /></td>
              <td>{String(row.name ?? "–")}<div className="sub">{String(row.symbol ?? "")}</div></td>
              <td>{String(row.industry_name ?? "–")}</td>
              <td className="reason wide">{String(row.revenue_structure ?? row.reason ?? "")}</td>
              <td><DartLink row={row} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 4차 일반 요건 — 상장 경과·관리종목·PER 기준별 충족, 시가총액, 판정·사유 */
function GeneralStageTable({ rows }: { rows: Record<string, unknown>[] }) {
  return (
    <div className="lab-tablewrap" style={{ marginTop: 6 }}>
      <table className="lab-table stage-table">
        <thead>
          <tr><th>판정</th><th>회사명</th><th>상장일</th><th className="n">경과(개월)</th><th className="n">시가총액</th><th className="n">PER</th>
            <th className="c">상장 경과</th><th className="c">관리종목 아님</th><th className="c">PER</th><th>사유</th></tr>
        </thead>
        <tbody>
          {passedFirst(rows).map((row, i) => {
            const g = (row.checks_general && typeof row.checks_general === "object" ? row.checks_general : {}) as Record<string, unknown>;
            return (
              <tr key={i} className={row.selected === true ? "pass" : "fail"}>
                <td><Verdict row={row} /></td>
                <td>{String(row.name ?? "–")}<div className="sub">{String(row.symbol ?? "")}</div></td>
                <td>{String(row.listed_date ?? "–")}</td>
                <td className="n">{row.months_listed == null ? "–" : String(row.months_listed)}</td>
                <td className="n">{eok(row.market_cap)}</td>
                <td className="n">{typeof row.per === "number" ? row.per.toFixed(2) : "–"}</td>
                <td className="c">{CHECK_MARK(g.listed)}</td><td className="c">{CHECK_MARK(g.supervision)}</td><td className="c">{CHECK_MARK(g.per)}</td>
                <td className="reason">{row.selected === true ? "—" : String(row.reason ?? "")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompanyTable({ companies }: { companies: Record<string, unknown>[] }) {
  if (!companies.length) return null;
  // 컬럼은 데이터에서 유도하되 흔한 키를 앞으로 (stage 별 스키마가 달라 고정 불가)
  const preferred = ["stock_code", "code", "symbol", "name", "company_name", "market", "industry", "industry_name", "ksic_code", "listing_date", "months_listed", "verdict", "revenue_pct", "note", "dart_url"];
  const keys = Object.keys(companies[0] || {});
  const cols = [...preferred.filter((k) => keys.includes(k)), ...keys.filter((k) => !preferred.includes(k))].slice(0, 8);
  const LABELS: Record<string, string> = {
    stock_code: "종목코드", code: "코드", symbol: "종목코드", name: "회사명", company_name: "회사명", market: "시장",
    industry: "산업분류", industry_name: "산업분류", ksic_code: "KSIC", listing_date: "상장일", months_listed: "경과(개월)", verdict: "판정",
    revenue_pct: "매출비중", note: "비고", dart_url: "원문",
  };
  return (
    <div className="lab-tablewrap" style={{ marginTop: 6 }}>
      <table className="lab-table">
        <thead><tr>{cols.map((c) => <th key={c}>{LABELS[c] || c}</th>)}</tr></thead>
        <tbody>
          {companies.slice(0, 200).map((row, i) => (
            <tr key={i}>
              {cols.map((c) => {
                const v = row[c];
                // URL 값(dart_url 등)은 잘린 문자열 대신 원문 링크로
                if (typeof v === "string" && /^https?:\/\//.test(v))
                  return <td key={c}><a href={v} target="_blank" rel="noreferrer">원문 ↗</a></td>;
                return <td key={c}>{typeof v === "number" ? fmtNum(v) : String(v ?? "–").slice(0, 120)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ③ 공시 선례 (주요사항보고서 외부평가) ──────────────────────────
/** 선례 목록은 최근 접수일부터 — 서버는 키워드 점수순으로 주지만 사용자는 최신 사례를 먼저 본다.
 *  같은 날은 접수번호 내림차순(나중에 낸 정정공시가 위). 화면·미리보기 시트·엑셀이 모두 이 순서를 쓴다. */
function sortPrecedentsByDateDesc(rows: PrecedentRow[]): PrecedentRow[] {
  const key = (r: PrecedentRow) => `${String(r.rcept_dt || "")}${String(r.rcept_no || "")}`;
  return [...rows].sort((a, b) => key(b).localeCompare(key(a)));
}

interface PrecedentRow {
  rcept_dt?: string;
  target_company_name?: string;
  corp_name?: string;
  external_evaluator_name?: string;
  report_nm?: string;
  score?: number;
  viewer_url?: string;
  external_opinion_attachment_url?: string;
  matched_keywords?: string[];
  /** 외부평가 유사기업 — 선정(selected)·제외 구분과 메모. 서버(major-report/search) 필드명 */
  peer_companies?: PrecedentPeer[];
  /** 이름만 있는 목록(peer_companies 추출 전 구버전 레코드) */
  peer_company_list?: string[];
  peer_company_count?: number;
  /** 예전 화면이 읽던 이름 — 서버는 이 필드를 주지 않는다(호환용으로만 남김) */
  external_peers?: PrecedentPeer[];
  context_snippets?: { keyword?: string; text?: string }[];
  target_company_business_summary_llm?: string;
  [k: string]: unknown;
}
type PrecedentPeer = { name?: string; selected?: boolean; note?: string | null };

/** 선례의 외부평가 유사기업 — 애드인과 같은 규칙: peer_companies 우선, 없으면 이름 목록을 모두 선정으로 */
export function precedentPeers(row: PrecedentRow): PrecedentPeer[] {
  if (row.peer_companies?.length) return row.peer_companies;
  if (row.external_peers?.length) return row.external_peers;
  return (row.peer_company_list || []).map((name) => ({ name, selected: true, note: null }));
}

function PrecedentTab() {
  const [business, setBusiness] = useLabState("pt.business", "");
  const [yearFrom, setYearFrom] = useLabState("pt.yearFrom", "");
  const [yearTo, setYearTo] = useLabState("pt.yearTo", "");
  const [evaluator, setEvaluator] = useLabState("pt.evaluator", "");
  const [busy, setBusy] = useState(false);
  const openPreview = useContext(PeerPreviewCtx);
  const [excelBusy, setExcelBusy] = useState(false);
  const [rows, setRows] = useLabState<PrecedentRow[] | null>("pt.rows", null);
  const [total, setTotal] = useLabState("pt.total", 0);
  const [err, setErr] = useLabState("pt.err", "");
  const sheet = useLiveSheet("pt.sheet");   // 검색 중 파란 커서 — 다른 탭과 동일한 감각

  // 지난 조회(공시 선례)를 누르면 ?tab=precedent&q=…&yf=…&yt=…&ev=… — 조건을 채우고 바로 다시 검색한다
  const { search } = useContext(PeerNavigationCtx);
  const ranSearch = useRef<string>("");
  useEffect(() => {
    const p = new URLSearchParams(search);
    if (p.get("tab") !== "precedent" || !p.has("q") || ranSearch.current === search || busy) return;
    ranSearch.current = search;
    const q = { business: p.get("q") || "", from: p.get("yf") || "", to: p.get("yt") || "", ev: p.get("ev") || "" };
    setBusiness(q.business); setYearFrom(q.from); setYearTo(q.to); setEvaluator(q.ev);
    void run({ business: q.business, yearFrom: q.from, yearTo: q.to, evaluator: q.ev });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const run = async (override?: { business: string; yearFrom: string; yearTo: string; evaluator: string }) => {
    const business_ = override ? override.business : business;
    const yearFrom_ = override ? override.yearFrom : yearFrom;
    const yearTo_ = override ? override.yearTo : yearTo;
    const evaluator_ = override ? override.evaluator : evaluator;
    if (business_.trim().length < 2 && !yearFrom_ && !yearTo_ && !evaluator_.trim()) {
      setErr("키워드·기간·평가기관 중 하나 이상을 입력하세요.");
      return;
    }
    setErr(""); setBusy(true); setRows(null);
    sheet.reset();
    sheet.append([
      { kind: "title", cells: ["공시 선례 검토 조서 — 주요사항보고서 외부평가"] },
      { kind: "note", cells: [`검색 조건: ${business_.trim() || "(키워드 없음)"}${yearFrom_ || yearTo_ ? ` · ${yearFrom_ || ""}~${yearTo_ || ""}` : ""}${evaluator_.trim() ? ` · 평가기관 ${evaluator_.trim()}` : ""}`] },
      { kind: "prog", cells: ["진행", "DART 주요사항보고서 원문·외부평가 의견서에서 유사 사업 선례 대조 중…"] },
    ]);
    try {
      const r = await precedentSearch({
        business_text: business_.trim().length >= 2 ? business_.trim() : undefined,
        year_from: yearFrom_ ? Number(yearFrom_) : undefined,
        year_to: yearTo_ ? Number(yearTo_) : undefined,
        evaluator: evaluator_.trim() || undefined,
      });
      const found = sortPrecedentsByDateDesc((r.results || []) as PrecedentRow[]);
      setRows(found);
      setTotal(r.result_count || r.results?.length || 0);
      recordPrecedentSearch({
        query: business_.trim(), year_from: yearFrom_ ? Number(yearFrom_) : null, year_to: yearTo_ ? Number(yearTo_) : null,
        evaluator: evaluator_.trim(),
      }, r.result_count || r.results?.length || 0);
      sheet.clearProg();
      sheet.append([{ kind: "title", cells: [`[결과] 외부평가 선례 ${r.result_count || found.length}건`] }]);
      sheet.append(found.slice(0, 10).map((row) => ({
        kind: "row" as const,
        cells: [String((row as Record<string, unknown>).rcept_dt || ""),
                String((row as Record<string, unknown>).target_company_name || ""),
                String((row as Record<string, unknown>).external_evaluator_name || "")],
      })));
    } catch (e) {
      sheet.clearProg();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="lab-card">
        <h2>주요사항보고서 외부평가기관 선례 검색</h2>
        <p className="lab-desc">
          합병·영업양수도 등 주요사항보고서에 첨부된 외부평가기관 평가의견서에서,
          비슷한 사업을 평가한 선례와 그때 쓰인 유사기업을 찾습니다.
        </p>
        <div className="lab-row">
          <div className="lab-field grow">키워드 / 사업의 내용
            <input value={business} disabled={busy} placeholder="의약품, 동물의약품"
              onChange={(e) => setBusiness(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void run(); }} />
          </div>
          <div className="lab-field">접수연도 (from)
            <input value={yearFrom} disabled={busy} placeholder="2022" style={{ width: 90 }}
              onChange={(e) => setYearFrom(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          </div>
          <div className="lab-field">(to)
            <input value={yearTo} disabled={busy} placeholder="2026" style={{ width: 90 }}
              onChange={(e) => setYearTo(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          </div>
          <div className="lab-field">평가기관 (선택)
            <input value={evaluator} disabled={busy} placeholder="삼일회계법인" style={{ width: 150 }}
              onChange={(e) => setEvaluator(e.target.value)} />
          </div>
          <Button variant="primary" disabled={busy} onClick={() => void run()}>
            {busy ? "검색 중…" : "선례 검색"}
          </Button>
        </div>
        {err && <p className="lab-error" role="alert" style={{ marginTop: 10 }}>{err}</p>}
      </section>

      <LiveSheet lines={sheet.lines} running={busy} />

      {rows && rows.length > 0 && (
        <div className="lab-actions" style={{ margin: "10px 0" }}>
          <button className="lab-chip" type="button" disabled={excelBusy}
            onClick={() => {
              setExcelBusy(true); setErr("");
              precedentExcel({ business_text: business.trim(), results: rows })
                .then((url) => {
                  recordLabFile({
                    url, kind: "excel", feature: "선례",
                    page: "/peer?tab=precedent", company: "",
                    label: decodeURIComponent((url.split("?")[0].split("/").pop() || "")) || "공시선례_조서.xlsx",
                  });
                  openPreview({ url, kind: "excel", title: "공시선례 조서 엑셀" });
                })
                .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
                .finally(() => setExcelBusy(false));
            }}>
            {excelBusy ? "엑셀 생성 중…" : "엑셀 파일로 ↓"}
          </button>
        </div>
      )}
      {rows && (
        <section className="lab-card">
          <h2>선례 {total}건</h2>
          {rows.length === 0 && <p className="lab-note">조건에 맞는 선례가 없습니다. 키워드를 넓혀 보세요.</p>}
          {rows.length > 0 && <PrecedentPeerFrequency rows={rows} />}
          {rows.map((r, i) => <PrecedentItem key={i} row={r} />)}
        </section>
      )}
    </>
  );
}

/** 선례 카드 제목 — 대상 회사를 뽑지 못한 공시(약 60%)는 '?' 대신 공시 회사와 거래 종류로 */
export function precedentTitle(row: PrecedentRow): string {
  const corp = String(row.corp_name || "").trim() || "공시 회사 미상";
  const target = String(row.target_company_name || "").trim();
  if (target) return `${target} ← ${corp}`;
  const kind = String(row.report_nm || "").replace(/^\s*(\[[^\]]*\]\s*)+/, "").match(/\(([^)]+)\)/)?.[1];
  return kind ? `${corp} · ${kind} (대상 회사 미기재)` : `${corp} (대상 회사 미기재)`;
}

export type PeerFrequency = { name: string; selected: number; mentioned: number; precedents: string[] };

/** 선례들의 외부평가 유사기업을 회사별로 모은다 — 실제 평가에 쓰인(선정) 횟수 순, 같으면 언급(제외 포함) 횟수 순 */
/** 같은 거래의 정정공시([기재정정]·[첨부정정])는 한 선례로 — 회사·대상·보고서 종류·평가기관이 같으면 같은 거래 */
export function precedentDealKey(row: PrecedentRow): string {
  const report = String(row.report_nm || "").replace(/^\s*(\[[^\]]*정정[^\]]*\]\s*)+/, "").trim();
  return [row.corp_name, row.target_company_name, report, row.external_evaluator_name].map((v) => String(v || "").trim()).join("|");
}

export function precedentPeerFrequency(rows: PrecedentRow[]): PeerFrequency[] {
  const map = new Map<string, PeerFrequency>();
  const counted = new Set<string>();   // 거래 × 회사 — 정정공시가 여러 건이어도 한 번만 센다
  for (const row of rows) {
    const deal = precedentDealKey(row);
    const label = `${String(row.rcept_dt || "").slice(0, 4) || "—"} ${String(row.target_company_name || row.corp_name || "")}`.trim();
    for (const peer of precedentPeers(row)) {
      const name = String(peer.name || "").trim();
      if (!name || counted.has(`${deal}::${name}`)) continue;
      counted.add(`${deal}::${name}`);
      const hit = map.get(name) || { name, selected: 0, mentioned: 0, precedents: [] };
      hit.mentioned += 1;
      if (peer.selected !== false) hit.selected += 1;
      if (!hit.precedents.includes(label)) hit.precedents.push(label);
      map.set(name, hit);
    }
  }
  return [...map.values()].sort((a, b) => b.selected - a.selected || b.mentioned - a.mentioned || a.name.localeCompare(b.name, "ko"));
}

function PrecedentPeerFrequency({ rows }: { rows: PrecedentRow[] }) {
  const list = precedentPeerFrequency(rows);
  const [all, setAll] = useState(false);
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 15);
  return (
    <div className="precedent-freq">
      <p className="precedent-freq-title">
        외부평가 유사기업 빈도 — {list.length}개사
        <span> · 선례 {new Set(rows.map(precedentDealKey)).size}건(정정공시는 한 건으로)에서 실제 평가에 쓰인 횟수 순 · 괄호는 제외 포함 언급 횟수</span>
      </p>
      <div className="lab-tablewrap">
        <table className="lab-table stage-table">
          <thead><tr><th className="n">#</th><th>회사명</th><th className="n">선정</th><th className="n">언급</th><th>쓰인 선례</th></tr></thead>
          <tbody>
            {shown.map((f, i) => (
              <tr key={f.name} className={f.selected ? "pass" : "fail"}>
                <td className="n">{i + 1}</td>
                <td className="name">{f.name}</td>
                <td className="n">{f.selected}회</td>
                <td className="n">({f.mentioned})</td>
                <td className="reason">{f.precedents.slice(0, 4).join(" · ")}{f.precedents.length > 4 ? ` 외 ${f.precedents.length - 4}건` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lab-actions">
        {list.length > 15 && <button type="button" className="lab-chip" onClick={() => setAll(!all)}>{all ? "상위 15개만" : `전체 ${list.length}개 보기`}</button>}
        <RelativeLink rows={list.filter((f) => f.selected > 0).map((f) => ({ name: f.name, selected: true })) as Record<string, unknown>[]} source={`공시 선례 빈도 상위 · ${rows.length}건`} />
      </div>
    </div>
  );
}

function PrecedentItem({ row }: { row: PrecedentRow }) {
  const [open, setOpen] = useState(false);
  const year = String(row.rcept_dt || "").slice(0, 4);
  const peers = precedentPeers(row);
  const peerCount = typeof row.peer_company_count === "number" && row.peer_company_count > 0 ? row.peer_company_count : peers.length;
  return (
    <div className="lab-acc">
      <button type="button" onClick={() => setOpen(!open)}>
        <span>
          [{year || "—"}] {precedentTitle(row)}
          <span className="acc-chip" style={{ marginLeft: 8 }}>
            {String(row.external_evaluator_name || "")} · 유사기업 {peerCount}개
          </span>
        </span>
        <span className="acc-chip">{open ? "접기" : "펼치기"}</span>
      </button>
      {open && (
        <div className="acc-body">
          {row.report_nm ? <p style={{ marginBottom: 8 }}>{String(row.report_nm)}</p> : null}
          {row.target_company_business_summary_llm ? (
            <p style={{ marginBottom: 8 }}>{String(row.target_company_business_summary_llm)}</p>
          ) : null}
          {(row.matched_keywords || []).length ? (
            <div className="lab-chips" style={{ marginBottom: 8 }}>
              {(row.matched_keywords || []).map((k) => <span className="lab-chip soft" key={k}>{k}</span>)}
            </div>
          ) : null}
          {peers.length ? (
            <div className="precedent-peers">
              <p className="precedent-peers-title">외부평가 유사기업 ({peerCount}개){peers.some((p) => p.selected === false) ? <span> · 취소선은 평가에서 제외된 회사</span> : null}</p>
              <div className="lab-chips">
                {peers.map((p, i) => (
                  <span key={i} className={`lab-chip${p.selected === false ? " off" : ""}`} title={p.note || ""}>
                    {String(p.name || "")}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <div className="lab-actions"><RelativeLink rows={peers.filter((p) => p.selected !== false) as Record<string, unknown>[]} source={`공시 선례 · ${row.target_company_name || row.corp_name || "평가의견서"}`} /></div>
          {(row.context_snippets || []).slice(0, 3).map((sn, i) => (
            <p className="lab-note" key={i} style={{ marginBottom: 4 }}>
              <strong>{sn.keyword}</strong> — {String(sn.text || "").slice(0, 240)}
            </p>
          ))}
          <div className="lab-actions">
            {row.viewer_url ? <a className="lab-chip" href={String(row.viewer_url)} target="_blank" rel="noreferrer">DART 원문 ↗</a> : null}
            {row.external_opinion_attachment_url ? (
              <a className="lab-chip" href={String(row.external_opinion_attachment_url)} target="_blank" rel="noreferrer">의견서 첨부 ↗</a>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
